// frontend/src/hooks/useWebSocket.js - Fixed version
import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from './useAuth'

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const ws = useRef(null)
  const reconnectTimeout = useRef(null)
  const messageHandlers = useRef(new Set())
  const { user } = useAuth()

  const WS_URL = import.meta.env.VITE_WS_URL || 'wss://api.testcrew.ai'

  const connect = useCallback(() => {
    if (!user?.id) {
      console.log('No user ID available for WebSocket connection')
      return
    }

    try {
      const wsUrl = `${WS_URL}/ws/${user.id}`
      console.log('Connecting to WebSocket:', wsUrl)
      
      ws.current = new WebSocket(wsUrl)

      ws.current.onopen = () => {
        console.log('WebSocket connected successfully')
        setIsConnected(true)
        setConnectionError(null)
      }

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          console.log('WebSocket message received:', message)
          
          // Notify all registered handlers
          messageHandlers.current.forEach(handler => {
            try {
              handler(message)
            } catch (error) {
              console.error('Error in message handler:', error)
            }
          })
          
          // Also dispatch custom event for backward compatibility
          const customEvent = new CustomEvent('websocket-message', {
            detail: message
          })
          window.dispatchEvent(customEvent)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason)
        setIsConnected(false)
          // ✅ ADDED: Reset any pending automation states
  if (window.automationState) {
    window.automationState.setIsStoppingExecution?.(false)
    window.automationState.setExecutionState?.('idle')
  }
        // Attempt to reconnect after 3 seconds if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          console.log('Scheduling reconnection...')
          reconnectTimeout.current = setTimeout(() => {
            console.log('Attempting to reconnect...')
            connect()
          }, 3000)
        }
      }

      ws.current.onerror = (error) => {
  console.error('WebSocket error:', error)
  setConnectionError('Connection failed')
  setIsConnected(false)
  
  // ✅ ADDED: Show user-friendly error
  if (window.toast) {
    window.toast.error('Connection lost. Please refresh and try again.')
  }
}

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      setConnectionError(error.message)
    }
  }, [user?.id, WS_URL])

  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...')
    
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current)
      reconnectTimeout.current = null
    }
    
    if (ws.current) {
      ws.current.close(1000, 'Component unmounting')
      ws.current = null
    }
    
    setIsConnected(false)
    setConnectionError(null)
  }, [])

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      console.log('Sending WebSocket message:', message)
      try {
        ws.current.send(JSON.stringify(message))
        return true
      } catch (error) {
        console.error('Failed to send message:', error)
        return false
      }
    } else {
      console.error('WebSocket is not connected. ReadyState:', ws.current?.readyState)
      return false
    }
  }, [])

  const addMessageHandler = useCallback((handler) => {
    messageHandlers.current.add(handler)
    return () => messageHandlers.current.delete(handler)
  }, [])

  // Auto-connect when user is available
  useEffect(() => {
    if (user?.id) {
      console.log('User available, connecting to WebSocket...')
      connect()
    } else {
      console.log('No user, disconnecting WebSocket...')
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [user?.id, connect, disconnect])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    isConnected,
    connectionError,
    sendMessage,
    connect,
    disconnect,
    addMessageHandler
  }
}

export default useWebSocket